import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { toast } from "sonner";
import PageHeader from "@/components/layout/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Mail, RefreshCcw, Send, RotateCcw, CheckCircle2, XCircle, Eye, Pencil } from "lucide-react";

const KIND_LABEL = {
  signup_notification: "Nouvelle inscription (DEV)",
  lease_contract: "Contrat de bail (locataire)",
};

export default function DevEmails() {
  const [tab, setTab] = useState("history");
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [filterKind, setFilterKind] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null); // {kind, subject, html, text}
  const [testOpen, setTestOpen] = useState(false);
  const [testForm, setTestForm] = useState({ kind: "signup_notification", to: "" });
  const [testSending, setTestSending] = useState(false);

  const loadStats = async () => {
    try {
      const { data } = await api.get("/dev/emails/stats");
      setStats(data);
    } catch { /* silent */ }
  };

  const loadLogs = async () => {
    setLogsLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterKind !== "all") params.set("kind", filterKind);
      if (filterStatus !== "all") params.set("status", filterStatus);
      const { data } = await api.get(`/dev/emails/logs?${params.toString()}`);
      setLogs(data || []);
    } catch {
      toast.error("Impossible de charger l'historique.");
    } finally {
      setLogsLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      const { data } = await api.get("/dev/emails/templates");
      setTemplates(data || []);
    } catch {
      toast.error("Impossible de charger les modèles.");
    }
  };

  useEffect(() => { loadStats(); loadLogs(); loadTemplates(); }, []);
  useEffect(() => { loadLogs(); /* eslint-disable-next-line */ }, [filterKind, filterStatus]);

  const openEditor = (tpl) => {
    setEditing({
      kind: tpl.kind,
      subject: tpl.custom_subject ?? tpl.default_subject,
      html: tpl.custom_html ?? tpl.default_html,
      text: tpl.custom_text ?? tpl.default_text,
      isCustom: tpl.custom_subject !== null && tpl.custom_subject !== undefined,
    });
  };

  const saveTemplate = async () => {
    try {
      await api.put(`/dev/emails/templates/${editing.kind}`, {
        subject: editing.subject,
        html: editing.html,
        text: editing.text,
      });
      toast.success("Modèle enregistré");
      setEditing(null);
      loadTemplates();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
    }
  };

  const resetTemplate = async (kind) => {
    try {
      await api.delete(`/dev/emails/templates/${kind}`);
      toast.success("Modèle réinitialisé sur les valeurs par défaut");
      loadTemplates();
    } catch {
      toast.error("Erreur de réinitialisation");
    }
  };

  const sendTest = async () => {
    if (!testForm.to || !testForm.to.includes("@")) {
      toast.error("Adresse email invalide");
      return;
    }
    setTestSending(true);
    try {
      const { data } = await api.post("/dev/emails/test", testForm);
      if (data.ok) {
        toast.success(`Email de test envoyé à ${testForm.to} (HTTP ${data.status_code})`);
        setTestOpen(false);
        loadLogs();
        loadStats();
      } else {
        toast.error(data.error || "Envoi échoué");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
    } finally {
      setTestSending(false);
    }
  };

  const kindBadge = (k) => (
    <Badge className="bg-teal-100 text-teal-800 border border-teal-200 hover:bg-teal-100 font-normal">
      {KIND_LABEL[k] || k}
    </Badge>
  );

  const statusBadge = (s) =>
    s === "sent" ? (
      <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 hover:bg-emerald-100">
        <CheckCircle2 className="w-3 h-3 mr-1" /> Envoyé
      </Badge>
    ) : (
      <Badge className="bg-rose-100 text-rose-800 border border-rose-200 hover:bg-rose-100">
        <XCircle className="w-3 h-3 mr-1" /> Échec
      </Badge>
    );

  const configWarning = useMemo(() => {
    if (!stats) return null;
    if (!stats.configured) {
      return "SendGrid non configuré : ajoutez SENDGRID_API_KEY et SENDER_EMAIL dans les variables d'environnement.";
    }
    return null;
  }, [stats]);

  return (
    <div>
      <PageHeader
        title="Notifications & Email"
        subtitle="Historique des emails envoyés, statistiques et personnalisation des modèles."
        testid="page-dev-emails"
        accent="teal"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => { loadStats(); loadLogs(); }} data-testid="btn-refresh-emails">
              <RefreshCcw className="w-4 h-4 mr-2" /> Actualiser
            </Button>
            <Button onClick={() => setTestOpen(true)} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="btn-send-test">
              <Send className="w-4 h-4 mr-2" /> Envoyer un test
            </Button>
          </div>
        }
      />

      {configWarning && (
        <Card className="p-4 rounded-xl border border-amber-200 bg-amber-50 mb-6 text-sm text-amber-900" data-testid="config-warning">
          {configWarning}
        </Card>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <Card className="p-5 rounded-xl border border-emerald-200 shadow-sm bg-gradient-to-br from-emerald-50 to-white">
          <div className="text-xs uppercase tracking-[0.18em] font-bold text-emerald-700">Total envoyés</div>
          <div className="font-heading text-3xl font-bold text-emerald-900 mt-1" data-testid="stat-total-sent">{stats?.total_sent ?? 0}</div>
        </Card>
        <Card className="p-5 rounded-xl border border-rose-200 shadow-sm bg-gradient-to-br from-rose-50 to-white">
          <div className="text-xs uppercase tracking-[0.18em] font-bold text-rose-700">Échecs</div>
          <div className="font-heading text-3xl font-bold text-rose-900 mt-1" data-testid="stat-total-failed">{stats?.total_failed ?? 0}</div>
        </Card>
        <Card className="p-5 rounded-xl border border-slate-200 shadow-sm bg-white">
          <div className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500">Expéditeur</div>
          <div className="font-heading text-base font-semibold text-slate-900 mt-1 truncate" data-testid="stat-sender">
            {stats?.sender_email || "—"}
          </div>
          <div className="text-xs text-slate-500 mt-1">Notifs DEV : {stats?.dev_notification_email || "—"}</div>
        </Card>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-100" data-testid="dev-emails-tabs">
          <TabsTrigger value="history" data-testid="tab-history">Historique</TabsTrigger>
          <TabsTrigger value="stats" data-testid="tab-stats">Statistiques par type</TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-templates">Modèles</TabsTrigger>
        </TabsList>

        <TabsContent value="history" className="mt-4 space-y-4">
          <Card className="p-4 rounded-xl border border-slate-200 shadow-sm bg-white flex flex-col md:flex-row md:items-end gap-3">
            <div className="w-56">
              <Label className="text-xs">Type</Label>
              <Select value={filterKind} onValueChange={setFilterKind}>
                <SelectTrigger data-testid="select-filter-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les types</SelectItem>
                  <SelectItem value="signup_notification">Nouvelle inscription</SelectItem>
                  <SelectItem value="lease_contract">Contrat de bail</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-48">
              <Label className="text-xs">Statut</Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger data-testid="select-filter-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="sent">Envoyé</SelectItem>
                  <SelectItem value="failed">Échec</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="text-xs text-slate-500 md:ml-auto">{logs.length} entrée(s) affichée(s)</div>
          </Card>

          <Card className="rounded-xl border border-slate-200 shadow-sm overflow-hidden bg-white">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Date</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Type</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Destinataire</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Objet</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Statut</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold text-right">Détails</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logsLoading && (
                  <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-12">Chargement…</TableCell></TableRow>
                )}
                {!logsLoading && logs.length === 0 && (
                  <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-12">
                    <Mail className="w-8 h-8 text-slate-300 mx-auto mb-3" />
                    Aucun email envoyé pour ces filtres.
                  </TableCell></TableRow>
                )}
                {!logsLoading && logs.map((l) => (
                  <TableRow key={l.id} className="hover:bg-slate-50/60" data-testid={`log-row-${l.id}`}>
                    <TableCell className="text-slate-600 whitespace-nowrap">{formatDateTime(l.sent_at)}</TableCell>
                    <TableCell>{kindBadge(l.kind)}</TableCell>
                    <TableCell className="font-medium text-slate-900 truncate max-w-[220px]">{l.to || "—"}</TableCell>
                    <TableCell className="text-slate-600 truncate max-w-[320px]">{l.subject || "—"}</TableCell>
                    <TableCell>{statusBadge(l.status)}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => setSelected(l)} data-testid={`btn-view-${l.id}`}>
                        <Eye className="w-4 h-4 text-slate-600" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="stats" className="mt-4">
          <Card className="rounded-xl border border-slate-200 shadow-sm overflow-hidden bg-white">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Type de notification</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-emerald-700 font-semibold text-right">Envoyés</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-rose-700 font-semibold text-right">Échecs</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold text-right">Taux de succès</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(stats?.by_kind || []).length === 0 && (
                  <TableRow><TableCell colSpan={4} className="text-center text-slate-500 py-12">Aucune donnée pour le moment.</TableCell></TableRow>
                )}
                {(stats?.by_kind || []).map((row) => {
                  const total = row.sent + row.failed;
                  const rate = total > 0 ? Math.round((row.sent / total) * 100) : 0;
                  return (
                    <TableRow key={row.kind} className="hover:bg-slate-50/60">
                      <TableCell className="font-medium text-slate-900">{row.label}</TableCell>
                      <TableCell className="text-right font-medium text-emerald-700" data-testid={`stat-${row.kind}-sent`}>{row.sent}</TableCell>
                      <TableCell className="text-right font-medium text-rose-700" data-testid={`stat-${row.kind}-failed`}>{row.failed}</TableCell>
                      <TableCell className="text-right font-semibold text-slate-900">{rate}%</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="templates" className="mt-4 space-y-4">
          {templates.map((t) => (
            <Card key={t.kind} className="p-5 rounded-xl border border-slate-200 shadow-sm bg-white" data-testid={`tpl-card-${t.kind}`}>
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                <div>
                  <div className="text-xs uppercase tracking-[0.18em] font-bold text-teal-700 mb-1">Modèle</div>
                  <h3 className="font-heading text-lg font-bold text-slate-900">{t.label}</h3>
                  <p className="text-sm text-slate-600 mt-1">
                    Variables disponibles :{" "}
                    {t.variables.map((v) => (
                      <code key={v} className="mx-1 px-1.5 py-0.5 bg-slate-100 border border-slate-200 rounded text-xs text-slate-800">
                        {"{{" + v + "}}"}
                      </code>
                    ))}
                  </p>
                  {t.custom_subject && (
                    <Badge className="mt-3 bg-orange-100 text-orange-800 border border-orange-200 hover:bg-orange-100">
                      Personnalisé — mis à jour {formatDateTime(t.updated_at)}
                    </Badge>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => openEditor(t)} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid={`btn-edit-${t.kind}`}>
                    <Pencil className="w-4 h-4 mr-2" /> Modifier
                  </Button>
                  {t.custom_subject && (
                    <Button variant="outline" onClick={() => resetTemplate(t.kind)} data-testid={`btn-reset-${t.kind}`}>
                      <RotateCcw className="w-4 h-4 mr-2" /> Réinitialiser
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </TabsContent>
      </Tabs>

      {/* Log detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl" data-testid="dialog-log-detail">
          <DialogHeader><DialogTitle className="font-heading">Détails de l'email</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-3 text-sm">
              <div><span className="text-slate-500">Envoyé le : </span><span className="font-medium">{formatDateTime(selected.sent_at)}</span></div>
              <div><span className="text-slate-500">Type : </span>{kindBadge(selected.kind)}</div>
              <div><span className="text-slate-500">Destinataire : </span><span className="font-medium">{selected.to}</span></div>
              <div><span className="text-slate-500">Objet : </span><span className="font-medium">{selected.subject}</span></div>
              <div><span className="text-slate-500">Statut : </span>{statusBadge(selected.status)} <span className="text-xs text-slate-500 ml-2">(HTTP {selected.status_code})</span></div>
              {selected.error && (
                <div className="rounded-lg bg-rose-50 border border-rose-200 p-3 text-rose-800 text-xs">
                  <div className="font-semibold mb-1">Erreur SendGrid</div>
                  {selected.error}
                </div>
              )}
              {selected.metadata && Object.keys(selected.metadata).length > 0 && (
                <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
                  <div className="text-xs uppercase tracking-[0.16em] font-bold text-slate-500 mb-2">Métadonnées</div>
                  <pre className="text-xs text-slate-700 whitespace-pre-wrap break-words">{JSON.stringify(selected.metadata, null, 2)}</pre>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelected(null)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template editor dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-3xl" data-testid="dialog-template-editor">
          <DialogHeader>
            <DialogTitle className="font-heading">Modifier le modèle {editing && KIND_LABEL[editing.kind]}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="space-y-2">
                <Label>Objet</Label>
                <Input
                  value={editing.subject}
                  onChange={(e) => setEditing({ ...editing, subject: e.target.value })}
                  data-testid="input-tpl-subject"
                />
              </div>
              <div className="space-y-2">
                <Label>Corps HTML</Label>
                <Textarea
                  rows={12}
                  value={editing.html}
                  onChange={(e) => setEditing({ ...editing, html: e.target.value })}
                  className="font-mono text-xs"
                  data-testid="input-tpl-html"
                />
                <p className="text-xs text-slate-500">Utilisez <code className="bg-slate-100 px-1 rounded">{"{{variable}}"}</code> pour insérer les valeurs dynamiques.</p>
              </div>
              <div className="space-y-2">
                <Label>Corps texte (fallback)</Label>
                <Textarea
                  rows={6}
                  value={editing.text}
                  onChange={(e) => setEditing({ ...editing, text: e.target.value })}
                  className="font-mono text-xs"
                  data-testid="input-tpl-text"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Annuler</Button>
            <Button onClick={saveTemplate} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="btn-save-tpl">
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send-test dialog */}
      <Dialog open={testOpen} onOpenChange={setTestOpen}>
        <DialogContent data-testid="dialog-send-test">
          <DialogHeader><DialogTitle className="font-heading">Envoyer un email de test</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>Modèle</Label>
              <Select value={testForm.kind} onValueChange={(v) => setTestForm({ ...testForm, kind: v })}>
                <SelectTrigger data-testid="select-test-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="signup_notification">Nouvelle inscription (DEV)</SelectItem>
                  <SelectItem value="lease_contract">Contrat de bail (locataire)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Destinataire</Label>
              <Input
                type="email"
                value={testForm.to}
                onChange={(e) => setTestForm({ ...testForm, to: e.target.value })}
                placeholder="votre-email@example.com"
                data-testid="input-test-to"
              />
              <p className="text-xs text-slate-500">L'email utilisera des données fictives et sera libellé « [TEST] » dans l'objet.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestOpen(false)}>Annuler</Button>
            <Button onClick={sendTest} disabled={testSending} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="btn-confirm-test">
              <Send className="w-4 h-4 mr-2" /> {testSending ? "Envoi…" : "Envoyer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
